/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 *
 * POC COPY — source of truth lives at
 *   cds-ai-service/frontend/src/views/dev-chat/chat-stream/StreamingMessage.jsx
 * See ./README.md for the extraction plan.
 */

import React, { useMemo } from 'react';

import CommandIntelligenceGenerationProgress from '../CommandIntelligenceGenerationProgress.jsx';
import CommandIntelligenceReasoning from '../CommandIntelligenceReasoning.jsx';
import { hasGenerationSnapshot, THINKING_GENERATION_SNAPSHOT } from './lrrProgress';
import { createChatMarkdownComponents } from './chatMarkdownComponents.jsx';
import { getStreamingMessageStyles } from './StreamingMessage.styles';
import { Box, useTheme } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const StreamingMessage = ({
  content,
  isStreaming,
  markdownDensity = 'comfortable',
  reasoningText = '',
  generationSnapshot = null,
}) => {
  const theme = useTheme();
  const styles = getStreamingMessageStyles(theme);
  const markdownComponents = useMemo(
    () => createChatMarkdownComponents(theme, { density: markdownDensity }),
    [theme, markdownDensity]
  );
  const hasText = Boolean(content?.trim());
  const activeSnapshot = isStreaming
    ? hasGenerationSnapshot(generationSnapshot)
      ? generationSnapshot
      : THINKING_GENERATION_SNAPSHOT
    : null;
  const completedSnapshot = !isStreaming && hasGenerationSnapshot(generationSnapshot) ? generationSnapshot : null;
  const showReasoning = Boolean(reasoningText?.trim()) && !isStreaming && !completedSnapshot && !hasText;

  return (
    <Box sx={styles.content}>
      {activeSnapshot ? <CommandIntelligenceGenerationProgress snapshot={activeSnapshot} isActive /> : null}

      {showReasoning ? <CommandIntelligenceReasoning isThinkingPhase={false} reasoningText={reasoningText} /> : null}

      {hasText ? (
        <Box sx={{ mt: activeSnapshot || completedSnapshot ? 1.25 : 0 }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {content}
          </ReactMarkdown>
        </Box>
      ) : null}

      {completedSnapshot ? (
        <Box sx={{ mt: hasText ? 1.25 : 0 }}>
          <CommandIntelligenceGenerationProgress
            snapshot={completedSnapshot}
            isActive={false}
            defaultExpanded={false}
          />
        </Box>
      ) : null}

      {isStreaming && hasText ? <Box component="span" sx={styles.caret} aria-hidden="true" /> : null}
    </Box>
  );
};

export default React.memo(StreamingMessage);
